import { useState } from 'react';
import { Button } from './ui/button';
import { Camera, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 2;

export const AnecdotePhotoUpload = ({ photos = [], onChange, bookId = '', context = 'client', inviteToken = '', maxPhotos = MAX_PHOTOS }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = maxPhotos - photos.length;
    if (remaining <= 0) {
      toast.error(`Maximo ${maxPhotos} fotos por anecdota`);
      return;
    }

    const toUpload = files.slice(0, remaining);
    setUploading(true);

    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: maximo 5MB`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('book_id', bookId);
        formData.append('context', context);
        // For the contributor flow, the invite_token is the auth credential
        // (the contributor doesn't have a session cookie). Sent as a query
        // param because the backend's UploadFile handler reads token from
        // request.query_params for multipart endpoints.
        const url = inviteToken
          ? `${API_URL}/api/anecdote-photo/upload?invite_token=${encodeURIComponent(inviteToken)}`
          : `${API_URL}/api/anecdote-photo/upload`;
        const res = await axios.post(url, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        onChange([...photos, { photo_id: res.data.photo_id, url: `${API_URL}${res.data.photo_url}`, storage_path: res.data.storage_path }]);
      } catch {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid="anecdote-photo-upload">
      <div className="flex items-center gap-2 flex-wrap">
        {photos.map((photo, idx) => (
          <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border group">
            <img
              src={photo.url}
              alt={`Foto ${idx + 1}`}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => removePhoto(idx)}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`remove-photo-${idx}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary flex flex-col items-center justify-center cursor-pointer transition-colors">
            {uploading ? (
              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
            ) : (
              <>
                <Camera className="w-5 h-5 text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground mt-0.5">Foto</span>
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
              data-testid="photo-file-input"
            />
          </label>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">{photos.length}/{maxPhotos} fotos (max 5MB cada una)</p>
    </div>
  );
};
